import * as THREE from 'three';
import { Player } from './Player';
import { World } from './World';
import { Input } from './Input';
import { GameAudio } from './Audio';
import { checkAABBCollision } from '../utils/helpers';
import {
  GameState,
  Difficulty,
  DIFFICULTY_SETTINGS,
  CAMERA_CONFIG,
} from './constants';

/**
 * Main game controller
 */
export class Game {
  // Three.js core
  private scene: THREE.Scene;
  private camera: THREE.PerspectiveCamera;
  private renderer: THREE.WebGLRenderer;

  // Game objects
  private player: Player;
  private world: World;
  private input: Input;
  private audio: GameAudio;

  // Game state
  private state: GameState = 'start';
  private score = 0;
  private distance = 0;
  private highScores: Record<Difficulty, number> = { easy: 0, normal: 0, hard: 0, insane: 0 };
  private lastMilestone = 0;

  // Difficulty
  private currentDifficulty: Difficulty = 'normal';

  // Speed progression
  private baseSpeed = 15;
  private currentSpeed = 15;
  private maxSpeed = 40;
  private speedIncreaseRate = 0.3;

  // Timing
  private lastTime = 0;

  // UI Elements
  private ui: GameUI;

  constructor() {
    this.scene = this.createScene();
    this.camera = this.createCamera();
    this.renderer = this.createRenderer();

    this.setupLighting();

    this.player = new Player();
    this.scene.add(this.player.mesh);

    this.world = new World(this.scene);
    this.input = new Input();
    this.audio = new GameAudio();

    this.ui = this.getUIElements();

    this.loadHighScores();
    this.setupUIListeners();
    this.updateSoundIcon();
    this.selectDifficulty('normal');

    window.addEventListener('resize', () => this.onResize());

    this.animate(0);
  }

  // ===========================================================================
  // Initialization
  // ===========================================================================

  private createScene(): THREE.Scene {
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x1a2a3a);
    scene.fog = new THREE.Fog(0x1a2a3a, 30, 100);
    return scene;
  }

  private createCamera(): THREE.PerspectiveCamera {
    const camera = new THREE.PerspectiveCamera(
      70,
      window.innerWidth / window.innerHeight,
      0.1,
      200
    );
    this.updateCameraPosition(camera);
    return camera;
  }

  private createRenderer(): THREE.WebGLRenderer {
    const canvas = document.getElementById('game-canvas') as HTMLCanvasElement;
    const renderer = new THREE.WebGLRenderer({ canvas, antialias: true });
    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    return renderer;
  }

  private setupLighting(): void {
    // Ambient light
    const ambientLight = new THREE.AmbientLight(0x6080a0, 0.6);
    this.scene.add(ambientLight);

    // Main directional light with shadows
    const directionalLight = new THREE.DirectionalLight(0xffffff, 1);
    directionalLight.position.set(10, 20, 10);
    directionalLight.castShadow = true;
    this.configureShadowMap(directionalLight);
    this.scene.add(directionalLight);

    // Hemisphere light for color gradient
    const hemisphereLight = new THREE.HemisphereLight(0x87ceeb, 0x2d4a5e, 0.4);
    this.scene.add(hemisphereLight);
  }

  private configureShadowMap(light: THREE.DirectionalLight): void {
    light.shadow.mapSize.width = 2048;
    light.shadow.mapSize.height = 2048;
    light.shadow.camera.near = 0.5;
    light.shadow.camera.far = 100;
    light.shadow.camera.left = -30;
    light.shadow.camera.right = 30;
    light.shadow.camera.top = 30;
    light.shadow.camera.bottom = -30;
  }

  private getUIElements(): GameUI {
    return {
      scoreDisplay: document.getElementById('score')!,
      startScreen: document.getElementById('start-screen')!,
      gameoverScreen: document.getElementById('gameover-screen')!,
      finalScoreDisplay: document.getElementById('final-score')!,
      highScoreDisplay: document.getElementById('high-score')!,
      soundToggle: document.getElementById('sound-toggle') as HTMLButtonElement,
      soundIcon: document.getElementById('sound-icon')!,
      difficultyButtons: document.querySelectorAll('.difficulty-btn') as NodeListOf<HTMLButtonElement>,
    };
  }

  // ===========================================================================
  // High Score Management
  // ===========================================================================

  private loadHighScores(): void {
    const difficulties: Difficulty[] = ['easy', 'normal', 'hard', 'insane'];
    for (const diff of difficulties) {
      this.highScores[diff] = parseInt(localStorage.getItem(`highScore_${diff}`) || '0');
    }
    this.updateHighScoreDisplay();
  }

  private saveHighScore(): void {
    if (this.score > this.highScores[this.currentDifficulty]) {
      this.highScores[this.currentDifficulty] = this.score;
      localStorage.setItem(`highScore_${this.currentDifficulty}`, this.score.toString());
    }
  }

  private updateHighScoreDisplay(): void {
    this.ui.highScoreDisplay.textContent = this.highScores[this.currentDifficulty].toString();
  }

  // ===========================================================================
  // UI Event Handlers
  // ===========================================================================

  private setupUIListeners(): void {
    document.getElementById('start-button')!.addEventListener('click', () => this.startGame());
    document.getElementById('retry-button')!.addEventListener('click', () => this.restartGame());
    document.getElementById('top-button')!.addEventListener('click', () => this.goToTop());
    document.getElementById('share-button')!.addEventListener('click', () => this.shareToX());

    this.ui.difficultyButtons.forEach((btn) => {
      btn.addEventListener('click', () => {
        this.selectDifficulty(btn.dataset.difficulty as Difficulty);
      });
    });

    this.ui.soundToggle.addEventListener('click', () => {
      this.audio.init();
      this.audio.toggleMute();
      this.updateSoundIcon();
    });

    window.addEventListener('keydown', (e) => {
      if (e.code === 'Space' || e.code === 'Enter') {
        if (this.state === 'start') this.startGame();
        else if (this.state === 'gameover') this.restartGame();
      }
    });
  }

  private selectDifficulty(difficulty: Difficulty): void {
    this.currentDifficulty = difficulty;

    this.ui.difficultyButtons.forEach((btn) => {
      btn.classList.toggle('selected', btn.dataset.difficulty === difficulty);
    });

    const settings = DIFFICULTY_SETTINGS[difficulty];
    this.baseSpeed = settings.baseSpeed;
    this.maxSpeed = settings.maxSpeed;
    this.speedIncreaseRate = settings.speedIncreaseRate;

    this.world.setObstacleGap(settings.minObstacleGap, settings.maxObstacleGap);
    this.updateHighScoreDisplay();
  }

  private updateSoundIcon(): void {
    const isMuted = this.audio.getMuted();
    this.ui.soundIcon.textContent = isMuted ? '🔇' : '🔊';
    this.ui.soundToggle.setAttribute('aria-label', isMuted ? 'サウンドオン' : 'サウンドオフ');
  }

  // ===========================================================================
  // Game State Management
  // ===========================================================================

  private startGame(): void {
    this.audio.init();
    this.audio.resume();
    this.audio.startBGM();

    this.currentSpeed = this.baseSpeed;
    this.state = 'playing';

    this.ui.startScreen.classList.add('hidden');
    this.ui.gameoverScreen.classList.add('hidden');
    this.input.clear();
  }

  private restartGame(): void {
    this.score = 0;
    this.distance = 0;
    this.lastMilestone = 0;
    this.currentSpeed = this.baseSpeed;
    this.ui.scoreDisplay.textContent = '0';

    this.player.reset();
    this.world.reset();

    this.startGame();
  }

  private gameOver(): void {
    this.state = 'gameover';

    this.audio.stopBGM();
    this.audio.playCrash();
    this.audio.playGameOver();

    this.saveHighScore();

    this.ui.finalScoreDisplay.textContent = this.score.toString();
    this.ui.highScoreDisplay.textContent = this.highScores[this.currentDifficulty].toString();
    this.ui.gameoverScreen.classList.remove('hidden');
  }

  private goToTop(): void {
    // Reset game state
    this.score = 0;
    this.distance = 0;
    this.lastMilestone = 0;
    this.currentSpeed = this.baseSpeed;
    this.ui.scoreDisplay.textContent = '0';

    // Reset game objects
    this.player.reset();
    this.world.reset();

    // Show start screen
    this.state = 'start';
    this.ui.gameoverScreen.classList.add('hidden');
    this.ui.startScreen.classList.remove('hidden');
    this.input.clear();
  }

  private shareToX(): void {
    const settings = DIFFICULTY_SETTINGS[this.currentDifficulty];
    const text = encodeURIComponent(
      `ROBO RUN【${settings.label}】で${this.score}m${settings.shareText}！${settings.shareEmoji} #ROBORUN`
    );
    const url = encodeURIComponent('https://roborun.maymai.dev');
    window.open(`https://twitter.com/intent/tweet?text=${text}&url=${url}`, '_blank', 'width=550,height=420');
  }

  // ===========================================================================
  // Game Loop
  // ===========================================================================

  private handleInput(): void {
    const actions = this.input.getActions();

    for (const action of actions) {
      switch (action) {
        case 'jump':
          if (this.player.jump(this.currentSpeed)) {
            this.audio.playJump();
          }
          break;
        case 'left':
          this.player.moveLeft();
          break;
        case 'right':
          this.player.moveRight();
          break;
      }
    }
  }

  private checkCollisions(): boolean {
    const playerBox = this.player.getBoundingBox();

    for (const obstacle of this.world.obstacles) {
      if (checkAABBCollision(playerBox, obstacle.getBoundingBox())) {
        return true;
      }
    }

    return false;
  }

  private update(deltaTime: number): void {
    if (this.state !== 'playing') return;

    this.handleInput();
    this.player.update(deltaTime, this.currentSpeed);
    this.world.update(deltaTime, this.currentSpeed, this.distance);

    // Update score
    this.distance += this.currentSpeed * deltaTime;
    this.score = Math.floor(this.distance);
    this.ui.scoreDisplay.textContent = this.score.toString();

    // Milestone sound every 100m
    const currentMilestone = Math.floor(this.score / 100);
    if (currentMilestone > this.lastMilestone) {
      this.lastMilestone = currentMilestone;
      this.audio.playMilestone();
    }

    // Speed progression
    if (this.currentSpeed < this.maxSpeed) {
      this.currentSpeed += this.speedIncreaseRate * deltaTime;
    }

    // Collision check
    if (this.checkCollisions()) {
      this.gameOver();
    }
  }

  private animate(currentTime: number): void {
    requestAnimationFrame((t) => this.animate(t));

    const deltaTime = Math.min((currentTime - this.lastTime) / 1000, 0.1);
    this.lastTime = currentTime;

    this.update(deltaTime);
    this.renderer.render(this.scene, this.camera);
  }

  // ===========================================================================
  // Camera Management
  // ===========================================================================

  private onResize(): void {
    this.camera.aspect = window.innerWidth / window.innerHeight;
    this.updateCameraPosition(this.camera);
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(window.innerWidth, window.innerHeight);
  }

  private updateCameraPosition(camera: THREE.PerspectiveCamera): void {
    const aspect = window.innerWidth / window.innerHeight;
    let config;

    if (aspect < 1) {
      config = CAMERA_CONFIG.PORTRAIT;
    } else if (aspect < 1.4) {
      config = CAMERA_CONFIG.TABLET;
    } else {
      config = CAMERA_CONFIG.DESKTOP;
    }

    camera.position.set(config.position.x, config.position.y, config.position.z);
    camera.lookAt(config.lookAt.x, config.lookAt.y, config.lookAt.z);
    camera.fov = config.fov;
  }
}

// ===========================================================================
// Types
// ===========================================================================

interface GameUI {
  scoreDisplay: HTMLElement;
  startScreen: HTMLElement;
  gameoverScreen: HTMLElement;
  finalScoreDisplay: HTMLElement;
  highScoreDisplay: HTMLElement;
  soundToggle: HTMLButtonElement;
  soundIcon: HTMLElement;
  difficultyButtons: NodeListOf<HTMLButtonElement>;
}
